'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Snap-carousel controller — the sliding-show + navigation mechanics extracted
 * from the view. Owns the scroll-track ref, per-slide refs, active-slide tracking
 * (`IntersectionObserver`), the tab-blur `focused` signal (so off-screen/background
 * videos pause), and imperative `scrollToIndex` navigation.
 *
 * The view stays pure layout: attach `scrollerRef` to the scroll track,
 * `registerSlide(index)` to each slide, read `activeIndex` / `focused` for the
 * dots + video auto-pause, and drive prev/next off `scrollToIndex`.
 */
export function useCarousel(
  slideCount: number,
  onActiveIndexChange?: (index: number) => void,
) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [focused, setFocused] = useState(true);

  // Tab visibility — pause all videos when the user switches tabs. Single
  // listener on the document; cheap.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setFocused(!document.hidden);
    setFocused(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // Track which slide is centered via IntersectionObserver. Computing the index
  // from `scrollLeft / slideWidth` works too but is jittery on iOS Safari
  // momentum scrolling — IO is the canonical pattern for snap carousels.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    if (slideCount <= 1) {
      setActiveIndex(0);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        // The slide with the highest intersection ratio is active. When the user
        // pauses mid-scroll between slides, ratios are similar — last one wins.
        let best: { index: number; ratio: number } | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const idx = slideRefs.current.indexOf(entry.target as HTMLDivElement);
          if (idx < 0) continue;
          if (best == null || entry.intersectionRatio > best.ratio) {
            best = { index: idx, ratio: entry.intersectionRatio };
          }
        }
        if (best) setActiveIndex(best.index);
      },
      {
        root: scroller,
        // 0.6 → a slide must be at least 60% visible to count as active; below
        // that the previous slide stays active, avoiding flicker between snaps.
        threshold: [0.6],
      },
    );

    for (const el of slideRefs.current) {
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [slideCount]);

  // Notify the caller of index changes — once per actual change.
  useEffect(() => {
    onActiveIndexChange?.(activeIndex);
  }, [activeIndex, onActiveIndexChange]);

  /** Ref-callback factory — attach `registerSlide(i)` to slide `i`. */
  const registerSlide = (index: number) => (el: HTMLDivElement | null) => {
    slideRefs.current[index] = el;
  };

  /** Smooth-scroll the given slide to the start of the track. */
  const scrollToIndex = (index: number) => {
    const target = slideRefs.current[index];
    if (!scrollerRef.current || !target) return;
    target.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  };

  return { scrollerRef, registerSlide, activeIndex, focused, scrollToIndex };
}
